import { Alert, Badge, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import styles from './urls.$id.module.css'
import { api, type Snapshot, type TrackedUrl } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/urls/$id')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: UrlDetailPage,
})

type TabId = 'pricing' | 'changes' | 'snapshots'

interface ChangeRecord {
  id: string
  changeType: string
  summary?: string
  detectedAt: string
  severity?: string
  beforeData?: Record<string, unknown>
  afterData?: Record<string, unknown>
}

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'danger' | 'warning'> = {
  active: 'success',
  error: 'danger',
  paused: 'warning',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatChangeType(type: string): string {
  return type.replace(/_/g, ' ')
}

function UrlDetailPage() {
  const { id } = Route.useParams()
  const [url, setUrl] = useState<TrackedUrl | null>(null)
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [changes, setChanges] = useState<ChangeRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabId>('pricing')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [snapshotsPage, setSnapshotsPage] = useState(1)
  const [snapshotsTotalPages, setSnapshotsTotalPages] = useState(1)
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false)
  const [isLoadingChanges, setIsLoadingChanges] = useState(false)

  const fetchUrl = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await api.getTrackedUrl(id)
      setUrl(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load URL details'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }, [id])

  const fetchSnapshots = useCallback(async () => {
    try {
      setIsLoadingSnapshots(true)
      const result = await api.listSnapshots(id, { page: snapshotsPage, limit: 10 })
      setSnapshots(result.data)
      setSnapshotsTotalPages(result.pagination.totalPages)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load snapshots')
    } finally {
      setIsLoadingSnapshots(false)
    }
  }, [id, snapshotsPage])

  const fetchChanges = useCallback(async () => {
    try {
      setIsLoadingChanges(true)
      const result = await api.getRecentChanges()
      // Filter changes for this URL if possible, or use all
      const allChanges = (result.data ?? []) as ChangeRecord[]
      setChanges(allChanges)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load changes')
    } finally {
      setIsLoadingChanges(false)
    }
  }, [])

  useEffect(() => {
    void fetchUrl()
  }, [fetchUrl])

  useEffect(() => {
    if (activeTab === 'snapshots') {
      void fetchSnapshots()
    }
  }, [activeTab, fetchSnapshots])

  useEffect(() => {
    if (activeTab === 'changes') {
      void fetchChanges()
    }
  }, [activeTab, fetchChanges])

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true)
      await api.refreshTrackedUrl(id)
      toast.success('Refresh triggered. New snapshot will be available shortly.')
      void fetchUrl()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to trigger refresh')
    } finally {
      setIsRefreshing(false)
    }
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
        <span>Loading URL details...</span>
      </div>
    )
  }

  if (error || !url) {
    return (
      <>
        <Link to="/urls" className={styles.backLink}>
          &larr; Back to Tracked URLs
        </Link>
        <Alert variant="danger">{error ?? 'URL not found'}</Alert>
        <div style={{ marginTop: '1rem' }}>
          <Button onClick={() => void fetchUrl()}>Retry</Button>
        </div>
      </>
    )
  }

  const latestSnapshot = snapshots.length > 0 ? snapshots[0] : null
  const extractedData = latestSnapshot?.extractedData ?? {}
  const extractedEntries = Object.entries(extractedData)

  const tabs: { id: TabId; label: string }[] = [
    { id: 'pricing', label: 'Current Pricing' },
    { id: 'changes', label: 'Change History' },
    { id: 'snapshots', label: 'Snapshots' },
  ]

  return (
    <>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <Link to="/urls" className={styles.backLink}>
            &larr; Back to Tracked URLs
          </Link>
          <h2 className={styles.urlTitle}>{url.displayName || url.url}</h2>
          {url.displayName && <p className={styles.urlAddress}>{url.url}</p>}
        </div>
        <div className={styles.headerActions}>
          <Badge variant={STATUS_BADGE_VARIANT[url.status] ?? 'default'}>{url.status}</Badge>
          <Button
            variant="secondary"
            size="sm"
            loading={isRefreshing}
            onClick={() => void handleRefresh()}
          >
            Refresh Now
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <Card className={styles.summaryCard}>
          <CardBody>
            <div className={styles.summaryValue}>{url.checkFrequency.replace('_', ' ')}</div>
            <div className={styles.summaryLabel}>Check Frequency</div>
          </CardBody>
        </Card>
        <Card className={styles.summaryCard}>
          <CardBody>
            <div className={styles.summaryValue}>{formatDate(url.lastCheckedAt)}</div>
            <div className={styles.summaryLabel}>Last Checked</div>
          </CardBody>
        </Card>
        <Card className={styles.summaryCard}>
          <CardBody>
            <div className={styles.summaryValue}>{formatDate(url.lastChangeAt)}</div>
            <div className={styles.summaryLabel}>Last Change</div>
          </CardBody>
        </Card>
        <Card className={styles.summaryCard}>
          <CardBody>
            <div className={styles.summaryValue}>{url.errorCount}</div>
            <div className={styles.summaryLabel}>Errors</div>
          </CardBody>
        </Card>
      </div>

      {/* Error Banner */}
      {url.status === 'error' && url.lastError && (
        <Alert variant="danger" style={{ marginBottom: '1.5rem' }}>
          <strong>Last error:</strong> {url.lastError}
        </Alert>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* Current Pricing Tab */}
        {activeTab === 'pricing' && (
          <>
            {isLoadingSnapshots && snapshots.length === 0 ? (
              <div className={styles.loadingContainer}>
                <Spinner />
                <span>Loading pricing data...</span>
              </div>
            ) : extractedEntries.length > 0 ? (
              <div className={styles.pricingSection}>
                <h3 className={styles.pricingSectionTitle}>Extracted Data</h3>
                <div className={styles.dataGrid}>
                  {extractedEntries.map(([key, value]) => (
                    <div key={key} className={styles.dataItem}>
                      <div className={styles.dataItemLabel}>{key}</div>
                      <div className={styles.dataItemValue}>
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                      </div>
                    </div>
                  ))}
                </div>
                {latestSnapshot && (
                  <p
                    style={{
                      marginTop: '1rem',
                      fontSize: '0.8125rem',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    From snapshot taken {formatDate(latestSnapshot.createdAt)}
                  </p>
                )}
              </div>
            ) : (
              <div className={styles.emptyTab}>
                <div className={styles.emptyTabIcon}>📊</div>
                <h3 className={styles.emptyTabTitle}>No pricing data yet</h3>
                <p className={styles.emptyTabDescription}>
                  Pricing data will appear here after the first snapshot is taken. Try clicking
                  &quot;Refresh Now&quot; to trigger a snapshot.
                </p>
              </div>
            )}
          </>
        )}

        {/* Change History Tab */}
        {activeTab === 'changes' && (
          <>
            {isLoadingChanges ? (
              <div className={styles.loadingContainer}>
                <Spinner />
                <span>Loading changes...</span>
              </div>
            ) : changes.length > 0 ? (
              <div className={styles.changesList}>
                {changes.map((change) => (
                  <div key={change.id} className={styles.changeItem}>
                    <div className={styles.changeItemLeft}>
                      <span className={styles.changeType}>
                        {formatChangeType(change.changeType)}
                      </span>
                      {change.summary && (
                        <span className={styles.changeTimestamp}>{change.summary}</span>
                      )}
                      <span className={styles.changeTimestamp}>
                        {formatDate(change.detectedAt)}
                      </span>
                    </div>
                    <div className={styles.changeItemRight}>
                      {change.severity && (
                        <Badge
                          variant={
                            change.severity === 'high'
                              ? 'danger'
                              : change.severity === 'medium'
                                ? 'warning'
                                : 'default'
                          }
                        >
                          {change.severity}
                        </Badge>
                      )}
                      <Link to="/urls/$id/diff/$changeId" params={{ id, changeId: change.id }}>
                        <Button variant="ghost" size="sm">
                          View Diff
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyTab}>
                <div className={styles.emptyTabIcon}>📝</div>
                <h3 className={styles.emptyTabTitle}>No changes detected</h3>
                <p className={styles.emptyTabDescription}>
                  When pricing changes are detected on this page, they will appear here.
                </p>
              </div>
            )}
          </>
        )}

        {/* Snapshots Tab */}
        {activeTab === 'snapshots' && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <Button
                variant="secondary"
                size="sm"
                loading={isRefreshing}
                onClick={() => void handleRefresh()}
              >
                Take Snapshot Now
              </Button>
            </div>
            {isLoadingSnapshots ? (
              <div className={styles.loadingContainer}>
                <Spinner />
                <span>Loading snapshots...</span>
              </div>
            ) : snapshots.length > 0 ? (
              <>
                <div className={styles.snapshotsList}>
                  {snapshots.map((snapshot) => (
                    <div key={snapshot.id} className={styles.snapshotItem}>
                      <div className={styles.snapshotInfo}>
                        <span className={styles.snapshotTimestamp}>
                          {formatDate(snapshot.createdAt)}
                        </span>
                        <span className={styles.snapshotHash}>
                          Hash: {snapshot.contentHash.substring(0, 12)}...
                        </span>
                      </div>
                      <div className={styles.snapshotActions}>
                        <Badge variant="success">Captured</Badge>
                      </div>
                    </div>
                  ))}
                </div>
                {snapshotsTotalPages > 1 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      marginTop: '1rem',
                    }}
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={snapshotsPage <= 1}
                      onClick={() => setSnapshotsPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <span
                      style={{
                        padding: '0.5rem',
                        fontSize: '0.875rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      Page {snapshotsPage} of {snapshotsTotalPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={snapshotsPage >= snapshotsTotalPages}
                      onClick={() => setSnapshotsPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyTab}>
                <div className={styles.emptyTabIcon}>📸</div>
                <h3 className={styles.emptyTabTitle}>No snapshots yet</h3>
                <p className={styles.emptyTabDescription}>
                  Snapshots are captured automatically based on your check frequency. Click
                  &quot;Take Snapshot Now&quot; to capture one immediately.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
